import type { Mastery, Missed } from "../save/schema";
import type { QuestionType } from "./questions/types";
import type { Answer } from "./round";

export type MasteryMap = Record<string, Mastery>;
export type MissedMap = Record<string, Missed>;

/** Right answers in a row after which a type counts as secure and comes up less. */
export const SECURE_STREAK = 5;

/**
 * Fold a round's answers into mastery and missed facts. A wrong answer adds
 * one to the fact's missed count; a right answer takes one off, and the fact
 * drops out at zero. Keeps the `limit` most-missed facts. Pure.
 */
export function recordAnswers(
  mastery: MasteryMap,
  missed: MissedMap,
  log: readonly Answer[],
  today: string,
  limit: number,
): { mastery: MasteryMap; missed: MissedMap } {
  const m: MasteryMap = { ...mastery };
  const x: MissedMap = { ...missed };
  for (const a of log) {
    const prev = m[a.typeId] ?? { seen: 0, correct: 0, streak: 0, last: today };
    m[a.typeId] = {
      seen: prev.seen + 1,
      correct: prev.correct + (a.correct ? 1 : 0),
      streak: a.correct ? prev.streak + 1 : 0,
      last: today,
    };
    const was = x[a.factKey];
    if (!a.correct) x[a.factKey] = { n: (was?.n ?? 0) + 1, type: a.typeId };
    else if (was && was.n > 1) x[a.factKey] = { ...was, n: was.n - 1 };
    else if (was) delete x[a.factKey];
  }
  const keys = Object.keys(x);
  if (keys.length > limit) {
    // Keep the most missed; among ties, the newest facts (added last).
    const keep = keys
      .map((k, i) => ({ k, n: x[k]!.n, i }))
      .sort((p, q) => q.n - p.n || q.i - p.i)
      .slice(0, limit);
    const trimmed: MissedMap = {};
    for (const { k } of keep) trimmed[k] = x[k]!;
    return { mastery: m, missed: trimmed };
  }
  return { mastery: m, missed: x };
}

/** Share of right answers, or null if never seen. */
export function accuracy(m: Mastery | undefined): number | null {
  return m && m.seen > 0 ? m.correct / m.seen : null;
}

/**
 * How strongly a mode favours a type: weak and untried types come up more,
 * secure ones (a long right streak) less. `strength` scales the effect: 1 is
 * Practice's full strength, 0.5 the gentler lean Battles use. Always
 * positive, so nothing vanishes.
 */
export function masteryWeight(mastery: MasteryMap, strength = 1) {
  return (type: QuestionType): number => {
    const m = mastery[type.id];
    if (!m || m.seen === 0) return 1 + strength;
    const smoothed = (m.correct + 1) / (m.seen + 2);
    const w = 1 + strength * 4 * (1 - smoothed);
    return m.streak >= SECURE_STREAK ? w / (1 + strength) : w;
  };
}

/** Practice's weighting, at full strength. */
export function practiceWeight(mastery: MasteryMap) {
  return masteryWeight(mastery, 1);
}
